import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ConfirmActionDialog from '../../../components/ConfirmActionDialog';
import { ControlpanelPrimaryButton, ControlpanelSecondaryButton, GrayIconButton } from '../../../components/StyledButton';
import { CustomDialog } from '../../../components/StyledComponents';
import LeaveApi from '../../../axios/LeaveAndHoliday/LeaveApi';
import { useAuth } from '../../../context/AuthContext';
import { format, isValid, parse } from 'date-fns';
import { getUserList } from '../../../axios/User/UserApi';
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarV2Icon16pxSvg from '../../../assets/images/icons/CalendarNoBGV2Icon16pxSvg';

const DEFAULT_FULL_DAY_MINUTES = 8 * 60;

const parseTimeStringToMinutes = (timeStr) => {
   if (!timeStr) return 0;
   const parts = String(timeStr).split(":").map(Number);
   const [h = 0, m = 0, s = 0] = parts;
   return h * 60 + m + Math.floor(s / 60);
};

const normalizeHoursPerLeaveDay = (value) => {
   if (!value) return "08:00:00";
   if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed || "08:00:00";
   }
   return "08:00:00";
};

const minutesToHhMmSs = (totalMinutes) => {
   const mins = Math.max(0, Math.round(Number(totalMinutes) || 0));
   const h = Math.floor(mins / 60);
   const m = mins % 60;
   return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
};

const daysToHhMmSs = (days, dayMins = DEFAULT_FULL_DAY_MINUTES) =>
   minutesToHhMmSs(Number(days || 0) * dayMins);

/** Ưu tiên numOfTime; fallback numOfDay (dữ liệu cũ) → HH:mm:ss */
const toTimeField = (timeValue, legacyDayValue, dayMins) => {
   if (timeValue) return normalizeHoursPerLeaveDay(timeValue);
   if (legacyDayValue != null && legacyDayValue !== "") {
      return daysToHhMmSs(Number(legacyDayValue) || 0, dayMins);
   }
   return "00:00:00";
};

/** numOfTime / hoursPerLeaveDaySnapshot → số ngày hiển thị */
const calcDaysFromTime = (numOfTime, hoursPerLeaveDaySnapshot) => {
   const timeMins = parseTimeStringToMinutes(numOfTime);
   const dayMins =
      parseTimeStringToMinutes(hoursPerLeaveDaySnapshot) || DEFAULT_FULL_DAY_MINUTES;
   if (dayMins <= 0) return 0;
   const days = timeMins / dayMins;
   const rounded = Math.round(days * 100) / 100;
   return Number.isInteger(rounded) ? rounded : rounded;
};

const DeleteLeaveDialog = ({ onClose, onRefresh, leaveInfo, isRequestMode = false, leaveTypesData = [] }) => {
   const { deleteLeaveRequest, addLeaveRequest, getLeaveById } = LeaveApi()

   const { t } = useTranslation();
   const { dateTimeFormat } = useAuth();
   const [leaveDetail, setLeaveDetail] = useState(leaveInfo);
   const [requestReason, setRequestReason] = useState("");
   const [usersData, setUsersData] = useState([]);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [loadingLeaveDetail, setLoadingLeaveDetail] = useState(false);

   const toDateForFormat = (value) => {
      if (!value) return null;
      if (value instanceof Date && isValid(value)) return value;
      if (typeof value === "string") {
         const clean = value.replace(/\u00A0/g, " ").trim();
         if (/^\d{4}-\d{2}-\d{2}T/.test(clean)) {
            const isoDate = new Date(clean);
            return isValid(isoDate) ? isoDate : null;
         }

         const parsed = parse(clean, dateTimeFormat, new Date());
         if (isValid(parsed)) return parsed;
      }
      return null;
   };

   const formatDateTime = (value) => {
      const dateValue = toDateForFormat(value);
      return dateValue ? format(dateValue, dateTimeFormat) : "-";
   };

   const getLeaveTypeLabel = (leaveTypeId) => {
      const leaveType = leaveTypesData?.find(
         (item) => String(item.leaveTypeId) === String(leaveTypeId)
      );
      if (!leaveType) return leaveDetail?.leaveType || leaveInfo?.leaveType || "-";

      const langId = Number(localStorage.getItem("i18nextLngId"));
      if (langId === 2) return leaveType.remarkEN || leaveType.remark || leaveType.leaveTypeName || "-";
      return leaveType.remark || leaveType.remarkEN || leaveType.leaveTypeName || "-";
   };

   const getFullNameByEmail = (email) => {
      if (!email) return "-";

      const normalizedEmail = String(email).trim().toLowerCase();

      const user = usersData.find(
         (u) =>
            u.email &&
            u.email.trim().toLowerCase() === normalizedEmail
      );

      // Nếu tìm được thì trả fullName, không thì fallback về email
      return user?.fullName || email;
   };

   useEffect(() => {
      const fetchUser = async () => {
         try {
            const payload = {
               statusId: 1,
               page: 1,
               pageSize: 1000000,
               status: null,
               langId: localStorage.getItem("i18nextLngId"),
            };
            const response = await getUserList(payload);
            setUsersData(response?.data?.data || []);
         } catch (error) {
            console.log(error);
         }
      };

      fetchUser();
   }, []);

   useEffect(() => {
      if (isRequestMode || !leaveInfo?.leaveId) return;

      const fetchLeaveDetail = async () => {
         try {
            setLoadingLeaveDetail(true);
            const response = await getLeaveById({
               leaveId: leaveInfo.leaveId,
               langId: localStorage.getItem("i18nextLngId"),
            });
            setLeaveDetail(response?.data?.data?.[0] || leaveInfo);
         } catch (error) {
            toast.error(error?.response?.data?.message || error.message);
         } finally {
            setLoadingLeaveDetail(false);
         }
      };

      fetchLeaveDetail();
   }, [isRequestMode, leaveInfo?.leaveId]);

   const oldData = useMemo(() => {
      const source = leaveDetail || leaveInfo || {};
      const snapshot = normalizeHoursPerLeaveDay(
         source.hoursPerLeaveDaySnapshot || "08:00:00"
      );
      const dayMins =
         parseTimeStringToMinutes(snapshot) || DEFAULT_FULL_DAY_MINUTES;

      return {
         leaveId: Number(source.leaveId ?? leaveInfo?.leaveId),
         userId: Number(source.userId ?? leaveInfo?.userId),
         shiftId: source.shiftId ?? leaveInfo?.shiftId ?? null,
         start: source.requestStartDate ?? source.start ?? leaveInfo?.requestStartDate ?? null,
         end: source.requestEndDate ?? source.end ?? leaveInfo?.requestEndDate ?? null,
         leaveTypeId: Number(source.leaveTypeId ?? leaveInfo?.leaveTypeId),
         leavePolicyId: source.leavePolicyId ?? null,
         numOfTime: toTimeField(source.numOfTime, source.numOfDay, dayMins),
         numOfTimeStart: toTimeField(
            source.numOfTimeStart,
            source.numOfDayStart ?? source.numOfDay,
            dayMins
         ),
         numOfTimeCarryStart: toTimeField(
            source.numOfTimeCarryStart,
            source.numOfDayCarryStart,
            dayMins
         ),
         numOfTimeEnd: toTimeField(source.numOfTimeEnd, source.numOfDayEnd, dayMins),
         numOfTimeCarryEnd: toTimeField(
            source.numOfTimeCarryEnd,
            source.numOfDayCarryEnd,
            dayMins
         ),
         hoursPerLeaveDaySnapshot: snapshot,
         requestReason: source.requestReason || leaveInfo?.requestReason || "",
         responseReason: source.responseReason || leaveInfo?.responseReason || "",
      };
   }, [leaveDetail, leaveInfo]);

   const displayDays = useMemo(
      () => calcDaysFromTime(oldData.numOfTime, oldData.hoursPerLeaveDaySnapshot),
      [oldData.numOfTime, oldData.hoursPerLeaveDaySnapshot]
   );

   const handleCloseDialog = () => {
      if (isSubmitting || loadingLeaveDetail) return;
      onClose();
   };

   const handleDeleteRequest = async () => {
      setIsSubmitting(true);
      try {
         // API delete không trả body — axios resolve (status 200) là thành công
         await deleteLeaveRequest({
            leaveId: leaveInfo.leaveId ?? leaveInfo.leaveRequestId,
            langId: localStorage.getItem("i18nextLngId"),
         });
         onClose();
         toast.success(t('common.remove-successfully'));
         onRefresh?.();
      } catch (error) {
         toast.error(error?.response?.data?.message || error.message);
      } finally {
         setIsSubmitting(false);
      }
   };

   const handleSubmit = async () => {
      setIsSubmitting(true);
      try {
         const response = await addLeaveRequest({
            leaveRequests: [
               {
                  requestType: "DELETE_LEAVE",
                  leaveId: oldData.leaveId,
                  userId: oldData.userId,
                  start: oldData.start,
                  end: oldData.end,
                  requestReason,
                  oldData,
               },
            ],
            langId: localStorage.getItem("i18nextLngId"),
         });

         if (
            response?.data?.message === 'Successful' ||
            response?.data?.message === 'Thành công'
         ) {
            onClose();
            toast.success(t('common.add-request-successfully'));
            onRefresh?.();
         } else {
            toast.error(response?.data?.message);
         }
      } catch (error) {
         toast.error(error?.response?.data?.message || error.message);
      } finally {
         setIsSubmitting(false);
      }
   };

   if (isRequestMode) {
      return (
         <ConfirmActionDialog
            open
            variant="delete"
            title={t('common.delete-leave')}
            subtitle={t('common.delete-leave-subtitle')}
            confirmText={t('button.delete')}
            loading={isSubmitting}
            onClose={handleCloseDialog}
            onConfirm={handleDeleteRequest}
         />
      );
   }

   const isDialogLoading = isSubmitting || loadingLeaveDetail;

   return (
      <CustomDialog maxWidth="md" open loading={isDialogLoading} onClose={handleCloseDialog} keepMounted>
         <DialogTitle>
            <div className='flex justify-between'>
               <div className='flex items-center gap-4'>
                  <p className='text-xl font-semibold text-[#1A1D1F]'>
                     {t('common.request-delete-leave')}
                  </p>
               </div>
               <GrayIconButton onClick={handleCloseDialog} disabled={isDialogLoading}>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>

         <DialogContent sx={{ borderTop: "1px solid #E5E7EB", pt: 2 }}>
            <div className="mt-5 rounded-md bg-[#F3F4F6] border-l-[6px] border-[#0057FF] p-4 mb-3">
               <div className="flex items-center gap-3 flex-wrap text-sm mb-1">
                  <span className="font-semibold text-[#111827]">{leaveDetail?.fullName || leaveDetail?.name || leaveInfo?.name}</span>
                  <span className="text-[#6B7280]">{t('common.leave-request-title')}</span>
                  <span className="text-[#16A34A]">{leaveDetail?.approvalStatus || t('common.approved')}</span>
               </div>
               <p className="text-sm font-semibold text-[#111827] mb-1">
                  {getLeaveTypeLabel(leaveDetail?.leaveTypeId ?? leaveInfo?.leaveTypeId)}
               </p>
               <div className="flex items-center gap-5 text-sm text-[#1F2937] flex-wrap">
                  <div className="flex items-center gap-2">
                     <CalendarV2Icon16pxSvg />
                     <span>
                        {formatDateTime(leaveDetail?.requestStartDate || leaveInfo?.requestStartDate)}
                        {" - "}
                        {formatDateTime(leaveDetail?.requestEndDate || leaveInfo?.requestEndDate)}
                     </span>
                  </div>
                  {displayDays ? (
                     <div className="flex items-center gap-2">
                        <AccessTimeIcon fontSize="small" className='text-[#6B7280]' />
                        <span>{`${displayDays} ${t('common.days')}`}</span>
                     </div>
                  ) : null}
               </div>
               <p className="text-sm text-[#6B7280] mt-1">
                  {t('common.approve-reject-by')} {getFullNameByEmail(leaveDetail?.modifiedBy)}
               </p>
            </div>

            <div className="">
               <label className="block text-sm text-[#111827] mb-2">
                  {t('common.reason')} <span className="text-[#DC2626]">*</span>
               </label>
               <textarea
                  className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm text-[#111827] outline-none resize-none"
                  rows={4}
                  placeholder={t('common.enter-reason')}
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
               />
            </div>
         </DialogContent>

         <DialogActions>
            <div className='flex gap-2 px-4 py-4'>
               <ControlpanelSecondaryButton onClick={handleCloseDialog} disabled={isDialogLoading}>
                  <p>{t('button.cancel')}</p>
               </ControlpanelSecondaryButton>
               <ControlpanelPrimaryButton
                  onClick={handleSubmit}
                  disabled={isDialogLoading || !requestReason.trim()}
               >
                  <p>{t('button.ok')}</p>
               </ControlpanelPrimaryButton>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default DeleteLeaveDialog;
